import classNames from 'classnames';
import { Select } from 'components/atoms/Form/Select';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { AIToolConsent } from 'types/users.types';
import { AIToolUsageConsentEnum } from 'types/users.types';

type AIToolConsentListProps = {
  consents: AIToolConsent;
  onChange: (consents: AIToolConsent) => void;
  className?: string;
  itemClassName?: string;
};

const AIToolConsentList: FC<AIToolConsentListProps> = ({
  consents,
  onChange,
  className,
  itemClassName,
}) => {
  const { t } = useTranslation();

  const toolOptions = useMemo(
    () => [
      {
        key: 'expenses' as const,
        title: t('ai_bot.tool_consent.expenses.title'),
        description: t('ai_bot.tool_consent.expenses.description'),
      },
      {
        key: 'revenues' as const,
        title: t('ai_bot.tool_consent.revenues.title'),
        description: t('ai_bot.tool_consent.revenues.description'),
      },
      {
        key: 'taxes' as const,
        title: t('ai_bot.tool_consent.taxes.title'),
        description: t('ai_bot.tool_consent.taxes.description'),
      },
      {
        key: 'summaries' as const,
        title: t('ai_bot.tool_consent.summaries.title'),
        description: t('ai_bot.tool_consent.summaries.description'),
      },
    ],
    [t],
  );

  const consentOptions = useMemo(
    () => [
      {
        label: t('ai_bot.tool_consent.allow'),
        value: AIToolUsageConsentEnum.allow,
      },
      {
        label: t('ai_bot.tool_consent.ask'),
        value: AIToolUsageConsentEnum.ask,
      },
      {
        label: t('ai_bot.tool_consent.deny'),
        value: AIToolUsageConsentEnum.deny,
      },
    ],
    [t],
  );

  const handleToolConsentChange = useCallback(
    (key: string, value: string) => {
      onChange({ ...consents, [key]: value });
    },
    [consents, onChange],
  );

  return (
    <div className={classNames('space-y-[-1px] px-3', className)}>
      {toolOptions.map((tool, index) => (
        <div
          key={tool.key}
          className={classNames(
            'py-3',
            itemClassName,
            index !== 0 && 'border-t border-primary-100',
          )}
        >
          <div className="flex items-center justify-between gap-12">
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-800 mb-1">
                {tool.title}
              </p>
              <p className="text-sm text-primary-600">{tool.description}</p>
            </div>

            <div className="flex-1 max-w-[150px]">
              <Select
                name={`${tool.key}-consent`}
                value={consents[tool.key]}
                size="sm"
                options={consentOptions}
                onChange={({ value }) =>
                  handleToolConsentChange(tool.key, value)
                }
                className="w-full"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AIToolConsentList;
